import { Injectable } from '@angular/core';
import { EntityKey, getEntityConfig, ENTITY_REGISTRY } from '../constants/entity-registry';
import { HttpMethod } from '../types/toast.types';

/**
 * MESSAGE BUILDER SERVICE
 *
 * Responsible for building success and error messages based on:
 * - HTTP method (POST, PUT, DELETE)
 * - Entity configuration from registry
 * - HTTP status codes
 *
 * Messages are retrieved from the entity registry, ensuring consistency
 * across the entire application.
 */
@Injectable({ providedIn: 'root' })
export class MessageBuilderService {

  /**
   * Build a success message for an HTTP operation.
   *
   * Uses the entity's pre-configured messages from the registry.
   * If entity is not found, returns a generic success message.
   *
   * @param method - HTTP method (POST, PUT, PATCH, DELETE)
   * @param entityKey - Entity key from registry
   * @returns Localized success message
   */
  buildSuccessMessage(method: HttpMethod, entityKey: EntityKey): string {
    const entity = getEntityConfig(entityKey);

    switch (method.toUpperCase()) {
      case 'POST':
        return entity.messages.success.created;

      case 'PUT':
      case 'PATCH':
        return entity.messages.success.updated;

      case 'DELETE':
        return entity.messages.success.deleted;

      default:
        return 'Operazione completata con successo';
    }
  }

  /**
   * Build an error message for an HTTP error.
   *
   * First checks if the entity has custom error messages for the status code.
   * Falls back to generic HTTP status messages.
   *
   * @param status - HTTP status code
   * @param entityKey - Optional entity key for entity-specific messages
   * @returns Localized error message
   */
  buildErrorMessage(status: number, entityKey?: EntityKey): string {
    // If entity is provided, check for custom error messages
    if (entityKey) {
      const customMessage = this.getCustomErrorMessage(status, entityKey);
      if (customMessage) {
        return customMessage;
      }
    }

    // Fall back to generic status-based messages
    return this.getGenericErrorMessage(status);
  }

  /**
   * Get custom error message from entity configuration.
   *
   * Maps common HTTP status codes to entity-specific error messages.
   *
   * @param status - HTTP status code
   * @param entityKey - Entity key from registry
   * @returns Custom error message or null if not configured
   */
  private getCustomErrorMessage(status: number, entityKey: EntityKey): string | null {
    const entity = getEntityConfig(entityKey);
    const errorMessages = entity.messages.error;

    if (!errorMessages) {
      return null;
    }

    switch (status) {
      case 404:
        return errorMessages.notFound || null;

      case 409:
        return errorMessages.conflict || null;

      case 400:
      case 422:
        return errorMessages.validation || null;

      case 403:
        return errorMessages.forbidden || null;

      default:
        return null;
    }
  }

  /**
   * Get generic error message based on HTTP status code.
   *
   * Provides user-friendly Italian messages for common HTTP errors.
   *
   * @param status - HTTP status code
   * @returns Localized error message
   */
  private getGenericErrorMessage(status: number): string {
    switch (status) {
      case 0:
      case 504:
        return 'Connessione non disponibile. Riprova tra un momento';

      case 400:
        return 'Dati non validi';

      case 401:
        return 'Accesso non autorizzato. Effettua nuovamente il login';

      case 403:
        return 'Non hai i permessi per questa operazione';

      case 404:
        return 'Risorsa non trovata';

      case 409:
        return 'Conflitto: operazione non può essere completata';

      case 422:
        return 'Dati forniti non validi';

      case 429:
        return 'Troppe richieste. Riprova tra un momento';

      case 500:
      case 502:
      case 503:
        return 'Errore del server. Riprova tra qualche minuto';

      default:
        return 'Si è verificato un errore. Riprova';
    }
  }

  /**
   * Get a list of all registered entities.
   * Useful for debugging or documentation.
   *
   * @returns Array of entity keys
   */
  getRegisteredEntities(): EntityKey[] {
    return Object.keys(ENTITY_REGISTRY) as EntityKey[];
  }

  /**
   * Check if an entity is registered.
   *
   * @param key - Entity key to check
   * @returns True if entity exists in registry
   */
  isEntityRegistered(key: string): key is EntityKey {
    return key in ENTITY_REGISTRY;
  }
}
